import { getRequest } from './api';
import { STORAGE_KEYS } from '../config/constants';

const getManagerData = () => {
  const userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
  const userDataStr = localStorage.getItem(STORAGE_KEYS.USER_DATA);
  const userData = userDataStr ? JSON.parse(userDataStr) : null;
  return { userId, userData };
};

export const managerService = {

  // ─── REQUESTS (unique-by-user, filtered to manager's pincodes) ───────────────
  // Returns one row per (userId + subCategoryId) with the customer user populated.
  // We filter by city match or directly by the manager's servingPinCodes on the user.
  getRequests: async (searchQuery = '') => {
    try {
      const { userId, userData } = getManagerData();
      const servingPinCodes = userData?.servingPinCodes || [];
      const city = userData?.city || '';

      // The /requests/unique-by-user endpoint does a MongoDB aggregate so we pass
      // a filter that targets the manager's city (same pattern as admin).
      const filter = {
        where: {
          ...(searchQuery ? {
            or: [
              { name: { like: searchQuery } },
              { mobileNumber: { like: searchQuery } },
              { pincode: { like: searchQuery } },
            ],
          } : {}),
          ...(city ? { city } : {}),
        },
        order: 'createdAt desc',
      };

      const response = await getRequest(
        `requests/unique-by-user?filter=${JSON.stringify(filter)}`
      );
      return response || [];
    } catch (error) {
      throw error;
    }
  },

  // ─── PARTNERS FOR A REQUEST (who received / accepted it) ─────────────────────
  // Same query as admin Requests.js → fetchAvailablePartners
  getRequestPartners: async (subCategoryId, userId) => {
    try {
      const filter = {
        where: { subCategoryId, userId },
        include: [{ relation: 'partner' }],
      };
      return await getRequest(`requests?filter=${JSON.stringify(filter)}`);
    } catch (error) {
      throw error;
    }
  },

  // ─── REQUEST LOGS (cancelled / not-accepted logs) ────────────────────────────
  // Same as admin CancledRequest.js — GET /requestlogs with user included.
  // Filtered to manager's city via the included user.city (we filter client-side
  // since requestlog model has no pincode field).
  getRequestLogs: async (page = 0, rowsPerPage = 10, searchQuery = '') => {
    try {
      const filter = {
        where: {
          ...(searchQuery ? {
            or: [
              { serviceName: { like: searchQuery } },
            ],
          } : {}),
        },
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        order: 'requestedAt desc',
        include: [{ relation: 'user' }],
      };
      return await getRequest(`requestlogs?filter=${JSON.stringify(filter)}`);
    } catch (error) {
      throw error;
    }
  },

  getRequestLogsCount: async (searchQuery = '') => {
    try {
      const where = {
        ...(searchQuery ? {
          or: [{ serviceName: { like: searchQuery } }],
        } : {}),
      };
      const res = await getRequest(`requestlogs/count?where=${JSON.stringify(where)}`);
      return res?.count || 0;
    } catch (error) {
      return 0;
    }
  },

  // ─── PARTNER CATEGORY STATS ──────────────────────────────────────────────────
  getPartnersWithServices: async () => {
    try {
      const { userData } = getManagerData();
      const servingPinCodes = userData?.servingPinCodes || [];
      const filter = {
        where: {
          userType: 'partner',
          pincode: { inq: servingPinCodes },
        },
        include: [{ relation: 'services' }],
      };
      return await getRequest(`users?filter=${JSON.stringify(filter)}`);
    } catch (error) {
      throw error;
    }
  },

  getCategories: async () => {
    try {
      return await getRequest('categories');
    } catch (error) {
      throw error;
    }
  },

  // ─── BOOKINGS (SERVICE REQUESTS) ─────────────────────────────────────────────
  getBookings: async (extraWhere = {}) => {
    try {
      const { userData } = getManagerData();
      const servingPinCodes = userData?.servingPinCodes || [];

      const filter = {
        where: {
          partnerPincode: { inq: servingPinCodes },  // lowercase 'c' — matches model field
          ...extraWhere,
        },
        order: 'createdAt DESC',
        include: [
          { relation: 'userReview' },
          { relation: 'service' },
          { relation: 'partner' },
        ],
      };
      return await getRequest(`service-requests?filter=${JSON.stringify(filter)}`);
    } catch (error) {
      throw error;
    }
  },
};

export default managerService;
